import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { deleteVoucherMutationOptions } from '@/features/vouchers/api/delete-voucher.api';
import { GET_LIST_VOUCHER_QUERY_KEY } from '@/features/vouchers/api/get-list-voucher.api';

export interface DeleteVoucerDialogProps extends BaseDialogProps {
  voucherId: string;
}

export default function DeleteVoucherDialog({
  dialog,
  voucherId,
}: DeleteVoucerDialogProps) {
  const { closeDeleteVoucherDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const deleteVoucherMutation = useMutation({
    ...deleteVoucherMutationOptions(),
    onSuccess: () => {
      toast.success('Voucher berhasil dihapus');
      queryClient.invalidateQueries({
        queryKey: [GET_LIST_VOUCHER_QUERY_KEY],
      });
      closeDeleteVoucherDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal menghapus voucher';
      toast.error(message);
    },
  });

  const isPending = deleteVoucherMutation.isPending;

  return (
    <AlertDialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState && !isPending) {
          closeDeleteVoucherDialog();
        }
      }}
    >
      <AlertDialogContent showCloseButton={false}>
        <AlertDialogHeader>
          <AlertDialogTitle>Hapus Voucher?</AlertDialogTitle>
          <AlertDialogDescription>
            Tindakan ini tidak dapat dibatalkan. Voucher akan dihapus secara
            permanen.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Batal</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isPending}
            onClick={() =>
              deleteVoucherMutation.mutate({ voucher_id: voucherId })
            }
          >
            {isPending ? 'Menghapus...' : 'Hapus'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
